'use client';

import { Alert, Toast } from '@fastfive-dev/fastfive-design-system/components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { PropsWithChildren, useState } from 'react';

import { LoginCompletionTracker } from '@/features/auth/shared/components/LoginCompletionTracker';
import { AuthSessionSync } from '@shared/components/AuthSessionSync';
import { GentooInitializer } from '@shared/components/GentooInitializer';
import { FloatingLayer } from '@shared/components/layout/FloatingLayer';
import { AttributionCapture } from '@shared/components/AttributionCapture';
import { GentooFormSubmitTracker } from '@/features/lead-form/components/GentooFormSubmitTracker';
import { LeadTrackingCapture } from '@/features/lead-form/components/LeadTrackingCapture';
import { MockingProvider } from '@shared/mocks/MockingProvider';
import { MswToggle } from '@shared/mocks/MswToggle';
import DataDogFeatureFlagProvider from '@shared/providers/DataDogFeatureFlagProvider';

import { FloatingLayerProvider } from './FloatingLayerContext';
import { ScriptLoaderProvider } from './ScriptLoaderContext';

export function AppProviders({ children }: PropsWithChildren) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return (
    <DataDogFeatureFlagProvider>
      <MockingProvider>
        <NuqsAdapter>
          <QueryClientProvider client={queryClient}>
            <ScriptLoaderProvider>
              <FloatingLayerProvider>
                <AuthSessionSync />
                <AttributionCapture />
                <LeadTrackingCapture />
                <GentooInitializer />
                <GentooFormSubmitTracker />
                {children}
                <FloatingLayer />
              </FloatingLayerProvider>
            </ScriptLoaderProvider>
          </QueryClientProvider>
        </NuqsAdapter>
        <MswToggle />
        <Alert.Container />
        <Toast.Container />
        {/* 토스트를 띄우므로 Toast.Container 옆에 둔다. 단 이 배치만으로 컨테이너 등록이
            보장되지는 않는다 (컴포넌트 주석 참고) — 실제 보장은 컴포넌트의 재시도 로직이 한다. */}
        <LoginCompletionTracker />
      </MockingProvider>
    </DataDogFeatureFlagProvider>
  );
}
